import classNames from 'classnames';
import first from 'lodash/first';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import { LinkListSectionVariant } from '../../LinkListSectionVariant.csharp';
import { getShortcutColorVariant, mapSectionItems } from '../../Utils/LinkListSectionUtils';
import type { LinkListSectionWithTabsViewModel } from '../../ViewModel/LinkListSectionWithTabsViewModel.csharp';
import { LinkListSectionHeaderWithWrapDetection } from '../LinkListSectionHeaderWithWrapDetection';
import sharedStyles from '../Shared/Styles/LinkListSection.module.css';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { LinkList } from '~/Partials/LinkList/LinkList';
import { StyledTabs } from '~/Partials/Tabs/StyledTabs';
import type { TabsItemProps } from '~/Partials/Tabs/Tabs';
import styles from './LinkListSectionTabsContent.module.css';

export const LinkListSectionTabsContent: React.FC<LinkListSectionWithTabsViewModel> = props => {
    const { title, description, variant, tabs, scrollableTabsTranslations, columnsLayout } = props;

    const theme = useTheme();
    const tabsColorVariant = variant === LinkListSectionVariant.Dark ? 'dark' : 'light';

    const tabData: TabsItemProps[] = tabs.map(tab => ({
        tabKey: uniqueId('tab__'),
        tabName: tab.name,
        tabContent: (
            <LinkList
                linkSize="large"
                links={mapSectionItems(tab.items)}
                linkVariant={getShortcutColorVariant(variant)}
                itemWrapperClassName={sharedStyles.LinkWrapper}
                className={sharedStyles.List}
                theme={{ ...theme, withBoxShadow: false }}
            />
        ),
    }));

    const TabListWrapper = React.useMemo<React.FC<React.PropsWithChildren>>(
        () => tabListWrapperProps => {
            const { children } = tabListWrapperProps;

            return (
                <LinkListSectionHeaderWithWrapDetection
                    className={classNames(sharedStyles.Header, styles.Header)}
                    wrappedClassName={styles['Header--wrapped']}
                >
                    {title && <h2 className={sharedStyles.Title}>{title}</h2>}
                    {description && <p className={sharedStyles.Description}>{description}</p>}
                    <div className={styles.TabListWrapper}>{children}</div>
                </LinkListSectionHeaderWithWrapDetection>
            );
        },
        [title, description]
    );

    const firstTab = first(tabData);

    if (!firstTab) {
        throw new Error('There must be at least one tab.');
    }

    return (
        <StyledTabs
            className={classNames(
                styles.Tabs,
                sharedStyles[`Variant--${variant}`],
                sharedStyles[`Variant--${columnsLayout}`]
            )}
            translations={scrollableTabsTranslations}
            name={title}
            selectedTab={firstTab.tabKey}
            tabs={tabData}
            colorVariant={tabsColorVariant}
            panelsClassName={styles.TabPanels}
            TabListWrapper={TabListWrapper}
        />
    );
};
